import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Briefcase, MapPin, Clock, Building2, ArrowRight, ChevronDown,
  ChevronUp, Search, Mail, Tag, Calendar, DollarSign, X, User, LogIn
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const JobOffersPage = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const { data } = await axios.get(`${API}/job-offers`);
        setOffers(data);
        const offerId = searchParams.get('id');
        if (offerId) setExpandedId(offerId);
      } catch (err) {
        console.error('Error fetching offers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, [searchParams]);

  const contractTypes = [...new Set(offers.map(o => o.contract_type))];

  const filtered = offers.filter(o => {
    const matchSearch = !search ||
      o.title.toLowerCase().includes(search.toLowerCase()) ||
      o.company.toLowerCase().includes(search.toLowerCase()) ||
      o.location.toLowerCase().includes(search.toLowerCase()) ||
      (o.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()));
    const matchType = !filterType || o.contract_type === filterType;
    return matchSearch && matchType;
  });

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="job-offers-page">

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#0F766E] to-[#0D9488] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#F59E0B] text-white text-xs font-semibold px-3 py-1 rounded-full mb-5">
              <Briefcase className="w-3 h-3" />
              OFFRES D'EMPLOI
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-4" data-testid="offers-title">
              Trouvez votre prochain poste en Polynésie
            </h1>
            <p className="text-lg text-white/70 leading-relaxed max-w-xl">
              AERH Recrutement Tahiti sélectionne les meilleures opportunités professionnelles pour les talents polynésiens. Toutes les candidatures sont traitées avec confidentialité.
            </p>
          </div>

          {/* Search bar + Espace Emploi CTA */}
          <div className="mt-10 max-w-2xl">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un poste, une entreprise, un lieu..."
                  className="pl-11 bg-white/10 border-white/20 text-white placeholder:text-white/40 h-12"
                  data-testid="search-offers"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-5">
              <Link to="/espace-emploi">
                <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-6" data-testid="cta-espace-emploi-candidat">
                  <User className="w-4 h-4 mr-2" /> Espace Candidat
                </Button>
              </Link>
              <Link to="/espace-emploi">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold px-6" data-testid="cta-espace-emploi-employeur">
                  <Building2 className="w-4 h-4 mr-2" /> Espace Employeur
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Filters + Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Filter chips */}
        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="filter-chips">
          <span className="text-sm text-gray-500 font-medium">Filtrer :</span>
          <button
            onClick={() => setFilterType('')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              !filterType ? 'bg-[#0D9488] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#0D9488] hover:text-[#0D9488]'
            }`}
            data-testid="filter-all"
          >
            Toutes ({offers.length})
          </button>
          {contractTypes.map(type => (
            <button
              key={type}
              onClick={() => setFilterType(filterType === type ? '' : type)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filterType === type ? 'bg-[#0D9488] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-[#0D9488] hover:text-[#0D9488]'
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        {/* Results count */}
        <p className="text-sm text-gray-500 mb-6" data-testid="results-count">
          {filtered.length} offre{filtered.length > 1 ? 's' : ''} disponible{filtered.length > 1 ? 's' : ''}
        </p>

        {/* Loading */}
        {loading && (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-4 border-[#0D9488] border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-gray-500 mt-4">Chargement des offres...</p>
          </div>
        )}

        {/* No results */}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
            <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucune offre trouvee</h3>
            <p className="text-gray-500 text-sm">Modifiez vos criteres de recherche ou consultez toutes nos offres.</p>
            {(search || filterType) && (
              <Button onClick={() => { setSearch(''); setFilterType(''); }} variant="outline" className="mt-4 border-[#0D9488] text-[#0D9488]">
                <X className="w-4 h-4 mr-2" /> Effacer les filtres
              </Button>
            )}
          </div>
        )}

        {/* Offers list */}
        <div className="space-y-4">
          {filtered.map((offer) => {
            const isExpanded = expandedId === offer.id;
            return (
              <div
                key={offer.id}
                className={`bg-white rounded-xl border transition-all ${isExpanded ? 'border-[#0D9488] shadow-lg' : 'border-gray-200 hover:border-[#0D9488]/50 hover:shadow-md'}`}
                data-testid={`offer-card-${offer.id}`}
              >
                {/* Header */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : offer.id)}
                  className="w-full text-left p-6 flex items-start gap-4"
                  data-testid={`offer-toggle-${offer.id}`}
                >
                  <div className="w-12 h-12 bg-[#0D9488]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-[#0D9488]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-bold text-[#1F2937] mb-1">{offer.title}</h3>
                        <p className="text-[#0D9488] font-semibold text-sm">{offer.company}</p>
                      </div>
                      <div className="flex-shrink-0 mt-1">
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
                      <span className="flex items-center gap-1.5 text-sm text-gray-500">
                        <MapPin className="w-3.5 h-3.5" /> {offer.location}
                      </span>
                      <span className="flex items-center gap-1.5 text-sm text-gray-500">
                        <Clock className="w-3.5 h-3.5" /> {offer.contract_type}
                      </span>
                      {offer.salary && (
                        <span className="flex items-center gap-1.5 text-sm text-gray-500">
                          <DollarSign className="w-3.5 h-3.5" /> {offer.salary}
                        </span>
                      )}
                    </div>
                    {/* Tags */}
                    {offer.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {offer.tags.map(tag => (
                          <span key={tag} className="bg-[#F8FAFC] border border-gray-200 text-gray-600 text-xs px-2.5 py-0.5 rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-gray-100 px-6 pb-6 pt-4" data-testid={`offer-detail-${offer.id}`}>
                    <p className="text-gray-600 leading-relaxed mb-6">{offer.summary}</p>

                    <div className="grid md:grid-cols-2 gap-8">
                      {/* Missions */}
                      {offer.missions?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold text-[#1F2937] uppercase tracking-wider mb-3 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-[#0D9488] rounded-full" /> Missions
                          </h4>
                          <ul className="space-y-2">
                            {offer.missions.map((m, i) => (
                              <li key={i} className="flex gap-2 text-sm text-gray-600">
                                <ArrowRight className="w-3.5 h-3.5 text-[#0D9488] flex-shrink-0 mt-0.5" />
                                {m}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Profile */}
                      {offer.profile?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-bold text-[#1F2937] uppercase tracking-wider mb-3 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full" /> Profil recherche
                          </h4>
                          <ul className="space-y-2">
                            {offer.profile.map((p, i) => (
                              <li key={i} className="flex gap-2 text-sm text-gray-600">
                                <ArrowRight className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                                {p}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Conditions */}
                    {offer.conditions?.length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-sm font-bold text-[#1F2937] uppercase tracking-wider mb-3 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-[#0D9488] rounded-full" /> Conditions
                        </h4>
                        <div className="flex flex-wrap gap-3">
                          {offer.conditions.map((c, i) => (
                            <span key={i} className="bg-[#0D9488]/10 text-[#0D9488] text-sm px-4 py-1.5 rounded-full font-medium">
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Dates */}
                    <div className="flex flex-wrap gap-6 mt-6 text-sm text-gray-500">
                      {offer.start_date && (
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" /> Prise de poste : {offer.start_date}
                        </span>
                      )}
                      {offer.deadline && (
                        <span className="flex items-center gap-1.5 text-[#F59E0B] font-medium">
                          <Clock className="w-3.5 h-3.5" /> Date limite : {offer.deadline}
                        </span>
                      )}
                    </div>

                    {/* CTA */}
                    <div className="mt-8 flex flex-wrap gap-4">
                      <a href={`mailto:${offer.contact_email}?subject=Candidature - ${offer.title}`}>
                        <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-7 py-5" data-testid={`offer-apply-${offer.id}`}>
                          <Mail className="w-4 h-4 mr-2" /> Postuler maintenant
                        </Button>
                      </a>
                      <p className="text-xs text-gray-400 self-center">
                        Toutes les candidatures sont traitées avec la plus stricte confidentialité.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Espace Emploi CTA */}
        <div className="mt-12 bg-gradient-to-br from-[#0F766E] to-[#0D9488] rounded-xl p-8 sm:p-12" data-testid="spontaneous-section">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-white mb-3">Votre Espace Emploi</h3>
              <p className="text-white/70 mb-6">
                Candidats, créez votre profil et postulez en 1 clic. Employeurs, publiez vos offres et accédez aux meilleurs talents polynésiens.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/espace-emploi">
                  <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-7 py-5" data-testid="spontaneous-apply">
                    Créer mon profil candidat <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/espace-emploi">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-semibold px-7 py-5">
                    Publier une offre <Building2 className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:grid grid-cols-2 gap-3 text-center">
              {[
                { val: 'Gratuit', label: 'Inscription candidat' },
                { val: '1 clic', label: 'Candidature rapide' },
                { val: '2 gratuites', label: 'Offres employeur' },
                { val: 'AERH', label: 'Modération qualité' },
              ].map((f, i) => (
                <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <p className="text-white font-bold text-lg">{f.val}</p>
                  <p className="text-white/60 text-xs">{f.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobOffersPage;
